import type { ProviderDetail } from '@katoa/schema-provider'

// Formatting and tidying for the provider page's practice-location list
// (VIT-18011). Pure functions — no router, no request, no I/O — so the rules the
// list depends on are unit-testable without rendering the route.
//
// The page used to render one location, via the flat `address` / `phone` /
// `facilityName` aliases the endpoint lifts off `locations[0]`. It now renders
// the whole `locations` array, which means two things the single-location path
// never had to handle: repeats (Ribbon can return the same site more than once)
// and locations carrying nothing worth showing.

/** One entry of the detail payload's `locations` array. */
export type ProviderLocation = ProviderDetail['locations'][number]

/** Trimmed value, or null when absent or whitespace-only. Mirrors provider.tsx's `isNonEmpty`. */
const trimmed = (value: string | null | undefined): string | null => {
  const next = value?.trim() ?? ''
  return next === '' ? null : next
}

/**
 * The address as one display line: "425 W 59th St, Suite 4, New York, NY 10019".
 *
 * Lifted out of provider.tsx, which returns the address structured so the
 * endpoint owns resolution and we own formatting. Every separator is
 * conditional, so a half-known address reads "New York, NY" rather than trailing
 * a stray comma or a lone space.
 *
 * `line2` is included, which the single-location version this replaces dropped.
 * That omission was invisible while one location rendered; with the whole list on
 * the page it is not, because the suite line is often the only thing separating
 * two practices in one building — without it they render as identical rows. It
 * also puts the page back in step with the JSON-LD, whose `postalAddress` has
 * always emitted `line2` in `streetAddress`.
 *
 * Returns '' when no part is known, which is the falsy the callers already test.
 */
export const formatAddressLine = (address: ProviderLocation['address'] | null | undefined): string => {
  const statePostal = [address?.state, address?.postalCode]
    .map(trimmed)
    .filter((part) => part != null)
    .join(' ')
  const cityStatePostal = [trimmed(address?.city), statePostal === '' ? null : statePostal]
    .filter((part) => part != null)
    .join(', ')

  return [trimmed(address?.line1), trimmed(address?.line2), cityStatePostal === '' ? null : cityStatePostal]
    .filter((part) => part != null)
    .join(', ')
}

/**
 * The number to call for this location, or null.
 *
 * `phones[0]`, because the endpoint puts Ribbon's `details === 'primary'` number
 * first. Deliberately this location's own number and never the top-level
 * `provider.phone` alias: every field of a location comes from the same source
 * record, which is what stops an address being paired with a number it did not
 * come from — the defect that made a single resolved address/phone pair unsafe
 * in the first place.
 */
export const primaryPhone = (location: ProviderLocation): string | null =>
  location.phones.map(trimmed).find((phone) => phone != null) ?? null

/** Whether a location carries anything the list could actually show. */
const isRenderable = (location: ProviderLocation): boolean =>
  trimmed(location.facilityName) != null || formatAddressLine(location.address) !== '' || primaryPhone(location) != null

/**
 * Case- and punctuation-insensitive identity for a location — what
 * `renderableLocations` dedupes on, and the React key for the rendered list.
 *
 * Punctuation is stripped by Unicode category, not by an ASCII range: `[^a-z0-9]`
 * would erase every letter of a name written in a non-Latin script, so two
 * distinct practices at one shared address would key identically and one would
 * vanish from the page.
 *
 * Every displayed field is part of the key, not the address alone. Two distinct
 * practices can share a street address with no suite line between them, and a
 * location can be known by nothing but its phone number — keying on less would
 * silently hide one of a pair the page has no other way to tell apart. Two rows
 * that differ nowhere a visitor can see are the only thing this removes, which is
 * what Ribbon actually returns.
 *
 * The trade is deliberate: an over-narrow key drops a real location invisibly,
 * while an over-wide one shows a near-duplicate. The second is the recoverable
 * failure.
 *
 * Unique across a deduped list by construction, which is why the list needs no
 * array-index keys: the payload carries no per-location id.
 */
export const locationKey = (location: ProviderLocation): string =>
  [
    location.facilityName,
    location.address.line1,
    location.address.line2,
    location.address.city,
    location.address.state,
    location.address.postalCode,
    primaryPhone(location),
  ]
    .map((part) => (part ?? '').toLowerCase().replace(/[^\p{L}\p{N}]+/gu, ''))
    .join('|')

/**
 * The locations worth rendering: repeats removed, empty entries dropped.
 *
 * Order is the payload's own — Ribbon-sourced and confidence-descending, with an
 * NPPES fallback — and the first occurrence of a repeat is the one kept, so the
 * highest-confidence copy survives. Nothing here re-sorts: the detail endpoint
 * takes no location anchor, so no ordering it could produce is search-relative,
 * and inventing one here would only disagree with the card the visitor clicked.
 */
export const renderableLocations = (locations: readonly ProviderLocation[]): ProviderLocation[] => {
  const seen = new Set<string>()

  return locations.filter((location) => {
    if (!isRenderable(location)) return false

    const key = locationKey(location)
    if (seen.has(key)) return false

    seen.add(key)
    return true
  })
}
